import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import './MyWebsites.css';
import { getStoredUser } from '../utils/authStorage';

const statusLabels = ['Healthy', 'Warning', 'Critical', 'Unknown'];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
  } catch {
    return '—';
  }
};

const normalizeStatus = (status) => {
  if (!status) return 'Unknown';
  const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  return statusLabels.includes(normalized) ? normalized : 'Unknown';
};

const PRIVILEGED_ROLES = ['admin', 'manager', 'technician'];

const MyWebsites = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authUser] = useState(() => getStoredUser());
  
  // Load plugin updates from localStorage on mount to persist across refreshes
  const [pluginUpdates, setPluginUpdates] = useState(() => {
    try {
      const stored = localStorage.getItem('webmonitor_plugin_updates');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if data is less than 5 minutes old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          console.log('[MyWebsites] Loaded plugin updates from localStorage');
          return parsed.data || {};
        }
      }
    } catch (error) {
      console.warn('[MyWebsites] Failed to load plugin updates from localStorage:', error);
    }
    return {};
  });
  
  const [pluginUpdatesLoading, setPluginUpdatesLoading] = useState(false);
  
  // Save plugin updates to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(pluginUpdates).length > 0) {
      try {
        localStorage.setItem('webmonitor_plugin_updates', JSON.stringify({
          data: pluginUpdates,
          timestamp: Date.now()
        }));
        console.log('[MyWebsites] Saved plugin updates to localStorage');
      } catch (error) {
        console.warn('[MyWebsites] Failed to save plugin updates to localStorage:', error);
      }
    }
  }, [pluginUpdates]);

  const canManageSites = PRIVILEGED_ROLES.includes(
    (authUser?.role || 'viewer').toLowerCase(),
  );

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/sites');
      setSites(data);
    } catch (err) {
      setError('Unable to load your websites right now.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const fetchPluginUpdates = useCallback(async (forceRefresh = false) => {
    // Set loading state (cached data will still show via renderPluginStatusCell)
    setPluginUpdatesLoading(true);
    
    const timeoutId = setTimeout(() => {
      // If loading takes more than 10 seconds, stop showing loading state
      console.warn('[MyWebsites] Plugin updates fetch timeout - stopping loading indicator');
      setPluginUpdatesLoading(false);
    }, 10000);
    
    try {
      console.log('[MyWebsites] Fetching plugin updates...', forceRefresh ? '(force refresh)' : '(cached)');
      const url = forceRefresh ? '/api/sites/plugin-updates?force=true' : '/api/sites/plugin-updates';
      // Timeout: 10 seconds for cached data (should be instant), 20 seconds for force refresh
      const timeout = forceRefresh ? 20000 : 10000;
      const { data } = await axios.get(url, { timeout });
      console.log('[MyWebsites] Received plugin updates response:', data);
      
      clearTimeout(timeoutId);
      
      const map = {};
      (data || []).forEach((item) => {
        // Handle both siteId and _id formats, convert to string for consistent matching
        // siteId might be an ObjectId, so we need to convert it properly
        let id = '';
        if (item.siteId) {
          id = String(item.siteId);
        } else if (item._id) {
          id = String(item._id);
        } else if (item.id) {
          id = String(item.id);
        }
        
        if (id) {
          // Normalize updates: null/undefined becomes 0, but preserve actual 0
          const updatesValue = item.updates !== null && item.updates !== undefined ? item.updates : 0;
          map[id] = {
            updates: updatesValue,
            total: item.total ?? 0,
            status: item.status || 'ok',
            error: item.error || null,
          };
        } else {
          console.warn('[MyWebsites] Plugin update item missing ID:', item);
        }
      });
      
      console.log('[MyWebsites] Plugin updates map:', map);
      console.log('[MyWebsites] Site IDs from sites:', sites.map(s => String(s._id)));
      console.log('[MyWebsites] Matching check:', {
        updateIds: Object.keys(map),
        siteIds: sites.map(s => String(s._id)),
        matches: sites.map(s => String(s._id)).filter(id => map[id] !== undefined)
      });
      
      // Preserve existing data and merge new data
      setPluginUpdates(prev => {
        const merged = { ...prev, ...map };
        console.log('[MyWebsites] Merged plugin updates:', Object.keys(merged).length, 'sites');
        console.log('[MyWebsites] Update counts:', Object.entries(merged).map(([id, info]) => ({
          id,
          updates: info.updates,
          status: info.status
        })));
        return merged;
      });
    } catch (pluginError) {
      clearTimeout(timeoutId);
      console.error('[MyWebsites] Unable to load plugin update summary:', pluginError);
      console.error('[MyWebsites] Error details:', {
        message: pluginError.message,
        response: pluginError.response?.data,
        status: pluginError.response?.status,
        code: pluginError.code
      });
      // Don't clear existing data on error - keep what we have
      // If we have no data and error, set empty entries for all sites to show "—"
      setPluginUpdates(prev => {
        if (Object.keys(prev).length === 0 && sites.length > 0) {
          const emptyMap = {};
          sites.forEach(site => {
            emptyMap[String(site._id)] = {
              updates: 0,
              total: 0,
              status: 'error',
              error: pluginError.message
            };
          });
          return emptyMap;
        }
        return prev;
      });
    } finally {
      setPluginUpdatesLoading(false);
    }
  }, [sites]); // Only depend on sites, not pluginUpdates

  // Fetch plugin updates independently of sites loading
  useEffect(() => {
    // Always fetch on mount (even if we have cached data, to keep it fresh)
    // Cached data is shown immediately, fresh data updates when available
    fetchPluginUpdates();
    
    // Set up interval to refresh every 60 seconds
    const interval = setInterval(() => {
      fetchPluginUpdates();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [fetchPluginUpdates]);
  
  // Also fetch when sites are loaded (in case sites load after plugin updates)
  useEffect(() => {
    if (sites.length > 0 && !loading) {
      // Re-fetch to ensure we have data for all sites
      // This ensures we get data for any new sites that were added
      fetchPluginUpdates();
    }
  }, [sites.length, loading, fetchPluginUpdates]);

  // Calculate uptime percentage from statusHistory
  const calculateUptime = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    
    // Get last 24 hours of history
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    const recentHistory = statusHistory.filter(
      entry => new Date(entry.timestamp).getTime() >= twentyFourHoursAgo
    );
    
    if (recentHistory.length === 0) return null;
    
    // Count healthy entries
    const healthyCount = recentHistory.filter(entry => entry.status === 'Healthy').length;
    const uptimePercent = (healthyCount / recentHistory.length) * 100;
    
    return Math.round(uptimePercent * 10) / 10; // Round to 1 decimal place
  };

  const normalizedSites = useMemo(
    () =>
      (sites || []).map((site) => {
        const uptimePercent = calculateUptime(site.statusHistory);
        
        return {
          ...site,
          name: site.name || 'Untitled Website',
          status: normalizeStatus(site.status),
          uptime: uptimePercent !== null ? `${uptimePercent}%` : '—',
          ownerEmail: site.ownerEmail || '—',
          lastCheckedDisplay: formatDate(site.lastChecked),
        };
      }),
    [sites],
  );

  const stats = useMemo(() => {
    const summary = {
      total: normalizedSites.length,
      Healthy: 0,
      Warning: 0,
      Critical: 0,
      Unknown: 0,
    };
    normalizedSites.forEach((site) => {
      summary[site.status] += 1;
    });
    return summary;
  }, [normalizedSites]);

  const filteredSites = useMemo(() => {
    const term = search.trim().toLowerCase();
    return normalizedSites
      .filter((site) => {
        const matchesTerm =
          !term ||
          site.name.toLowerCase().includes(term) ||
          (site.url || '').toLowerCase().includes(term);
        const matchesStatus = statusFilter === 'all' || site.status === statusFilter;
        return matchesTerm && matchesStatus;
      })
      .sort((a, b) => {
        if (a.status === b.status) {
          return (b.lastChecked || '').localeCompare(a.lastChecked || '');
        }
        return statusLabels.indexOf(a.status) - statusLabels.indexOf(b.status);
      });
  }, [normalizedSites, search, statusFilter]);

  const renderPluginStatusCell = (site) => {
    // Convert site ID to string for consistent matching
    const siteIdStr = String(site._id || site.id || '');
    
    // Try to find plugin update info for this site
    const info = pluginUpdates[siteIdStr];
    
    // If we have data, show it immediately (even if still loading more)
    if (info) {
      // If there's an error status, show dash
      if (info.status === 'error' || info.error) {
        return '—';
      }
      
      // If status is 'pending', show 0 (data is being fetched in background)
      if (info.status === 'pending') {
        return '0';
      }
      
      // Get updates count (should be a number, default to 0)
      const updatesCount = typeof info.updates === 'number' ? info.updates : 0;
      
      // If there are updates, show as clickable link
      if (updatesCount > 0) {
        return (
          <Link
            to={`/sites/${site._id}`}
            state={{ activeTab: 'plugins' }}
            className="plugin-status-link"
          >
            {updatesCount}
          </Link>
        );
      }

      // Show 0 if we have valid data but no updates
      return '0';
    }
    
    // If still loading and we have NO data at all, show loading indicator
    // But only for the first 3 seconds, then show dash
    if (pluginUpdatesLoading && Object.keys(pluginUpdates).length === 0) {
      // Use a ref or state to track when we started loading
      // For now, just show "..." briefly, then "—"
      return '...';
    }
    
    // If no info after loading completes, show dash
    // This means either no data was returned, or the site doesn't have plugin data
    return '—';
  };

  return (
    <div className="dashboard-container my-websites-page">
      <Sidebar />
      <div className="my-websites-main">
        <header className="my-websites-header">
          <div>
            <p className="eyebrow">PORTFOLIO OVERVIEW</p>
            <h1>My Websites</h1>
            <p className="subtitle">
              Track uptime, SEO, and health insights for every property you manage.
            </p>
          </div>
          <div className="header-actions">
            <button 
              type="button" 
              className="ghost-button" 
              onClick={() => {
                fetchSites();
                fetchPluginUpdates(true); // Force refresh on manual refresh
              }}
            >
              <span className="button-icon">🔄</span>
              Refresh data
            </button>
            {canManageSites && (
              <Link to="/add-website" className="primary-button">
                <span className="button-icon">+</span>
                Add website
              </Link>
            )}
          </div>
        </header>

        <section className="site-stats-bar">
          <div className="stat-item">
            <span className="stat-label">TOTAL SITES</span>
            <span className="stat-number">{stats.total}</span>
            <span className="stat-support">Tracked in WebMonitor</span>
          </div>
          {statusLabels.slice(0, 3).map((label) => (
            <div className={`stat-item status-${label.toLowerCase()}`} key={label}>
              <span className="stat-label">{label}</span>
              <span className="stat-number">{stats[label]}</span>
              <span className="stat-support">Current state</span>
            </div>
          ))}
        </section>

        <section className="site-tools-panel">
          <div className="search-input">
         
            <input
              type="text"
              placeholder="Search by name or URL"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="filters">
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All</option>
              {statusLabels.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="site-table-card">
          <div className="table-heading">
            <h2>Website inventory</h2>
            <span className="table-meta">{filteredSites.length} websites</span>
          </div>

          {error && <div className="inline-error">{error}</div>}

          {loading ? (
            <div className="loading-state">Loading your websites…</div>
          ) : filteredSites.length === 0 ? (
            <div className="empty-state">
              <h3>No websites found</h3>
              <p>Try changing your filters or add a new property to monitor.</p>
              {canManageSites && (
                <Link to="/add-website" className="primary-button">
                  Add website
                </Link>
              )}
            </div>
          ) : (
            <div className="table-scroll">
              <table className="sites-table">
                <thead>
                  <tr>
                    <th>Website</th>
                    <th>Status</th>
                    <th>Uptime</th>
                    <th>Owner Email</th>
                    <th>Last scan</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSites.map((site) => (
                    <tr key={site._id}>
                      <td>
                        <div className="site-name-cell">
                          <span className="globe-icon">🌐</span>
                          <div>
                            <Link to={`/sites/${site._id}`} className="site-name-link">
                              <strong className="site-name">{site.name}</strong>
                            </Link>
                            <div className="site-url">{site.url}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-pill ${site.status.toLowerCase()}`}>
                          {site.status}
                        </span>
                      </td>
                      <td>{site.uptime}</td>
                      <td>{site.ownerEmail}</td>
                      <td>{site.lastCheckedDisplay}</td>
                      <td className="table-actions">
                        <Link to={`/sites/${site._id}`} className="text-link">
                          View details →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

    </div>
  );
};

export default MyWebsites;

